import { liquidGlassPillShadowSx, liquidGlassSurfaceFillSx } from '@focuslee/shared-mini-app';
import { Box } from '@mui/material';
import { FC, useEffect, useRef } from 'react';
import { CommonIcon } from './CommonIcon';

interface LiquidGlassSearchBarProps {
  value: string;
  onChange: (value: string) => void;
  onClose: () => void;
  placeholder: string;
  ariaLabel: string;
  height?: number;
}

export const LiquidGlassSearchBar: FC<LiquidGlassSearchBarProps> = ({
  value,
  onChange,
  onClose,
  placeholder,
  ariaLabel,
  height = 44,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  return (
    <Box
      sx={{
        position: 'relative',
        height,
        flex: 1,
        minWidth: 0,
        borderRadius: '999px',
        overflow: 'visible',
        pointerEvents: 'auto',
        ...liquidGlassPillShadowSx,
      }}
    >
      <Box
        sx={{
          height: '100%',
          borderRadius: 'inherit',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          ...liquidGlassSurfaceFillSx,
        }}
        onPointerDown={event => {
          if (!(event.target as HTMLElement).closest('button')) inputRef.current?.focus();
        }}
      >
        <CommonIcon
          icon="search"
          type="filled"
          size={18}
          color="var(--tg-theme-text-color)"
          style={{ flexShrink: 0, marginLeft: 14, marginRight: 8 }}
        />
        <Box
          component="input"
          ref={inputRef}
          type="text"
          inputMode="search"
          enterKeyHint="search"
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          placeholder={placeholder}
          aria-label={ariaLabel}
          value={value}
          onChange={e => onChange(e.target.value)}
          sx={{
            flex: 1,
            minWidth: 0,
            height: '100%',
            border: 'none',
            outline: 'none',
            bgcolor: 'transparent',
            color: 'var(--tg-theme-text-color)',
            fontSize: 16,
            px: 0,
            py: 0,
            boxSizing: 'border-box',
            appearance: 'none',
            WebkitAppearance: 'none',
            '&::placeholder': { color: 'var(--tg-theme-hint-color)', opacity: 1 },
          }}
        />
        <Box
          component="button"
          type="button"
          aria-label="Close search"
          onClick={onClose}
          sx={{
            flexShrink: 0,
            width: height - 8,
            height: height - 8,
            mr: '4px',
            border: 'none',
            borderRadius: '50%',
            bgcolor: 'transparent',
            color: 'var(--tg-theme-text-color)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            WebkitTapHighlightColor: 'transparent',
            outline: 'none',
            '&:focus-visible': { outline: '2px solid var(--tg-theme-link-color)', outlineOffset: 2 },
          }}
        >
          <CommonIcon icon="close" type="filled" size={20} color="var(--tg-theme-text-color)" />
        </Box>
      </Box>
    </Box>
  );
};
